import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Star, XCircle } from "lucide-react";
import LogoType from "@/components/LogoType";
import BookingCalendar from "@/components/BookingCalendar";
import PendulumIntake from "@/components/PendulumIntake";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [flow, setFlow] = useState("reading");
  const [tierName, setTierName] = useState("Reading");
  const [quantity, setQuantity] = useState(1);
  const [fixedDelivery, setFixedDelivery] = useState(null);
  const params = new URLSearchParams(window.location.search);
  // PayPal returns to the success page with ?token=<orderId> (session_id kept for compatibility)
  const sessionId = params.get("session_id") || params.get("token");

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setFlow(data.flow || "reading");
          if (data.tier_name) setTierName(data.tier_name);
          setQuantity(data.quantity || 1);
          setFixedDelivery(data.delivery_only || null);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep trying */ }
      attempts += 1;
      if (attempts >= 20) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center">
      <Link to="/" className="mb-10"><LogoType size="md" /></Link>

      {state === "checking" && (
        <div className="gold-frame rounded-sm bg-[#1F130E] p-10 max-w-md w-full flex flex-col items-center" data-testid="payment-checking">
          <Loader2 className="w-10 h-10 text-[#C9A24B] animate-spin mb-6" />
          <h1 className="font-display text-3xl text-[#EFE3CC]">Confirming your payment…</h1>
          <p className="mt-3 text-[#B7A488] font-light">One moment while the stars align.</p>
        </div>
      )}

      {state === "paid" && flow === "reading" && (
        <BookingCalendar sessionId={sessionId} tierName={tierName} fixedDelivery={fixedDelivery} />
      )}

      {state === "paid" && flow === "pendulum" && (
        <PendulumIntake sessionId={sessionId} tierName={tierName} quantity={quantity} />
      )}

      {state === "paid" && !["reading", "pendulum"].includes(flow) && (
        <div className="gold-frame rounded-sm bg-[#1F130E] p-10 max-w-md w-full flex flex-col items-center" data-testid="payment-success">
          <div className="w-16 h-16 rounded-full border border-[#C9A24B]/40 flex items-center justify-center glow-gold mb-6">
            <Star className="w-7 h-7 text-[#C9A24B]" fill="#C9A24B" strokeWidth={0} />
          </div>
          <h1 className="font-display text-3xl text-[#EFE3CC]">Thank you ✦</h1>
          <p className="mt-3 text-[#B7A488] font-light">Your order is confirmed and will be lovingly prepared.</p>
          <Link to="/" className="mt-8 rounded-full border border-[#C9A24B]/45 px-8 py-3 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all">Back to Home</Link>
        </div>
      )}

      {state === "failed" && (
        <div className="gold-frame rounded-sm bg-[#1F130E] p-10 max-w-md w-full flex flex-col items-center" data-testid="payment-failed">
          <XCircle className="w-12 h-12 text-[#C77E8A] mb-6" strokeWidth={1.2} />
          <h1 className="font-display text-3xl text-[#EFE3CC]">We couldn't confirm payment</h1>
          <p className="mt-3 text-[#B7A488] font-light">If you were charged, please reach out and we'll make it right.</p>
          <Link to="/pricing" className="mt-8 rounded-full border border-[#C9A24B]/45 px-8 py-3 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all">Try Again</Link>
        </div>
      )}
    </div>
  );
}
